import React, { useCallback, useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Modal } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { X, SlidersHorizontal, MapPin, Dog, TreePine, Sun, Cloud, Shield, Star, ChevronDown, ChevronUp } from 'lucide-react-native';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { ExploreFilters, DEFAULT_FILTERS } from '@/hooks/useExploreFilters';
import { useTrailFilterCount } from '@/lib/queries/explore';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';

// ─── Filter section definitions ─────────────────────────────────

const SORT_OPTIONS = [
  { key: 'match_score' as const, label: 'filters.bestMatch' },
  { key: 'distance' as const, label: 'filters.closest' },
  { key: 'newest' as const, label: 'filters.newest' },
  { key: 'rating' as const, label: 'filters.highestRated' },
];

const DIFFICULTY_OPTIONS = [
  { key: 'easy', label: 'filters.easy' },
  { key: 'moderate', label: 'filters.moderate' },
  { key: 'hard', label: 'filters.hard' },
];

const DISTANCE_FROM_YOU_OPTIONS = [
  { key: '1km', label: 'filters.under1km', min: 0, max: 1000 },
  { key: '5km', label: 'filters.oneTo5km', min: 1000, max: 5000 },
  { key: '15km', label: 'filters.fiveTo15km', min: 5000, max: 15000 },
  { key: '35km', label: 'filters.fifteenTo35km', min: 15000, max: 35000 },
];

const LENGTH_OPTIONS = [
  { key: 'short', label: 'filters.short', min: 0, max: 2000 },
  { key: 'medium', label: 'filters.medium', min: 2000, max: 5000 },
  { key: 'long', label: 'filters.long', min: 5000, max: null },
];

const TAG_OPTIONS = [
  { key: 'Beach', label: 'explore.beach', icon: Umbrella },
  { key: 'Forest', label: 'explore.forest', icon: TreePine },
  { key: 'Road', label: 'explore.road', icon: TrafficCone },
  { key: 'Cliff', label: 'explore.cliff', icon: Mountain },
];

const RATING_OPTIONS = [
  { key: 3, label: '3+' },
  { key: 4, label: '4+' },
  { key: 4.5, label: '4.5+' },
];

import { Umbrella, TrafficCone, Mountain } from 'lucide-react-native';

interface FilterSheetProps {
  visible: boolean;
  filters: ExploreFilters;
  onApply: (filters: ExploreFilters) => void;
  onClose: () => void;
  trailCount?: number;
  /** Auth token + dog for the live /trail/search/count lookup. */
  token?: string;
  dogId?: string;
  /** User's location — fallback center for the live count. */
  userLocation?: { latitude: number; longitude: number } | null;
}

export default function FilterSheet({ visible, filters, onApply, onClose, trailCount, token, dogId, userLocation }: FilterSheetProps) {
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const [local, setLocal] = useState<ExploreFilters>({ ...filters });
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});

  // Sync local state when the sheet opens
  useEffect(() => {
    if (visible) {
      setLocal({ ...filters });
    }
  }, [visible]);

  // Live count of trails matching the DRAFT filters — updates as the user
  // toggles, before apply. Falls back to the applied `trailCount` while loading.
  const liveCountQuery = useTrailFilterCount({
    token,
    filters: local,
    locationCoords: userLocation ?? undefined,
    dogId,
    enabled: visible,
  });
  const displayCount = liveCountQuery.data ?? trailCount;

  const toggleSection = useCallback((key: string) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const update = useCallback(<K extends keyof ExploreFilters>(key: K, value: ExploreFilters[K]) => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    setLocal((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearAll = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    setLocal({ ...DEFAULT_FILTERS });
  }, []);

  const handleApply = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    onApply(local);
    onClose();
  }, [local, onApply, onClose]);

  const activeCount = (() => {
    let c = 0;
    if (local.sort !== DEFAULT_FILTERS.sort) c++;
    if (local.tags.length > 0) c += local.tags.length;
    if (local.difficulty !== null) c++;
    if (local.minDistance !== null || local.maxDistance !== null) c++;
    if (local.minUserDistance !== null || local.maxUserDistance !== null) c++;
    if (local.offLeash) c++;
    if (local.wildlife) c++;
    if (local.minRating !== null) c++;
    if (local.lowDogTraffic) c++;
    if (local.lowFootTraffic) c++;
    if (local.lowExposure) c++;
    return c;
  })();

  // ─── Reusable sub-components (with accessibility) ────────────

  const SectionHeader = ({ title, sectionKey, count }: { title: string; sectionKey: string; count?: number }) => (
    <TouchableOpacity
      style={s.sectionHeader}
      onPress={() => toggleSection(sectionKey)}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={`${title} filter section${count ? `, ${count} active` : ''}`}
      accessibilityHint={collapsedSections[sectionKey] ? t('filters.expandSection') : t('filters.collapseSection')}
    >
      <View style={s.sectionHeaderLeft}>
        <Text style={s.sectionTitle}>{title}</Text>
        {count ? <View style={s.sectionBadge}><Text style={s.sectionBadgeText}>{count}</Text></View> : null}
      </View>
      {collapsedSections[sectionKey] ? <ChevronDown size={20} color={theme.textMuted} /> : <ChevronUp size={20} color={theme.textMuted} />}
    </TouchableOpacity>
  );

  const Chip = ({ label, active, onPress, icon, accessibilityLabel }: { label: string; active: boolean; onPress: () => void; icon?: React.ReactNode; accessibilityLabel?: string }) => (
    <TouchableOpacity
      style={[s.chip, active && s.chipActive]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: active }}
      accessibilityLabel={accessibilityLabel || label}
    >
      {icon}
      <Text style={[s.chipText, active && s.chipTextActive]}>{label}</Text>
    </TouchableOpacity>
  );

  const Toggle = ({ label, value, onToggle, icon }: { label: string; value: boolean | null; onToggle: () => void; icon?: React.ReactNode }) => (
    <TouchableOpacity
      style={[s.toggleRow, value && s.toggleRowActive]}
      onPress={onToggle}
      activeOpacity={0.7}
      accessibilityRole="switch"
      accessibilityState={{ checked: !!value }}
      accessibilityLabel={label}
    >
      <View style={s.toggleLeft}>
        {icon}
        <Text style={[s.toggleLabel, value && s.toggleLabelActive]}>{label}</Text>
      </View>
      <View style={[s.toggleSwitch, value && s.toggleSwitchActive]}>
        {value && <View style={s.toggleDot} />}
      </View>
    </TouchableOpacity>
  );



  return (
    <Modal visible={visible} animationType="slide" transparent={false} onRequestClose={onClose} accessibilityViewIsModal>
      <View style={s.sheet}>
        <View style={{ height: insets.top }} />
        {/* Drag handle */}
        <View style={s.dragHandleContainer}>
          <View style={s.dragHandle} />
        </View>

        {/* Header */}
        <View style={s.header}>
          <Text style={s.headerTitle}>{t('filters.title')}{activeCount > 0 ? ` (${activeCount})` : ''}</Text>
          {activeCount > 0 ? (
            <TouchableOpacity onPress={clearAll} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel={t('filters.clearAll')}>
              <Text style={s.clearAllText}>{t('filters.clearAll')}</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity onPress={onClose} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel={t('common.close')}>
              <X size={24} color={theme.textMuted} />
            </TouchableOpacity>
          )}
        </View>

        <ScrollView style={s.scrollContent} showsVerticalScrollIndicator={false}>
          {/* ── Sort ─────────────────────────────────────────── */}
          <SectionHeader title={t('filters.sortBy')} sectionKey="sort" />
          {!collapsedSections['sort'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {SORT_OPTIONS.map((opt) => (
                  <Chip key={opt.key} label={t(opt.label)} active={local.sort === opt.key} onPress={() => update('sort', opt.key)} accessibilityLabel={`Sort by ${t(opt.label)}`} />
                ))}
              </View>
            </View>
          )}

          {/* ── Difficulty ───────────────────────────────────── */}
          <SectionHeader title={t('filters.difficulty')} sectionKey="difficulty" />
          {!collapsedSections['difficulty'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {DIFFICULTY_OPTIONS.map((opt) => (
                  <Chip key={opt.key} label={t(opt.label)} active={local.difficulty === opt.key} onPress={() => update('difficulty', local.difficulty === opt.key ? null : opt.key)} accessibilityLabel={`${t(opt.label)} difficulty`} />
                ))}
              </View>
            </View>
          )}

          {/* ── Trail Length ─────────────────────────────────── */}
          <SectionHeader title={t('filters.trailLength')} sectionKey="length" />
          {!collapsedSections['length'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {LENGTH_OPTIONS.map((opt) => {
                  const isActive = local.minDistance === opt.min && local.maxDistance === opt.max;
                  return (
                    <Chip key={opt.key} label={t(opt.label)} active={isActive} onPress={() => {
                      if (isActive) {
                        update('minDistance', null);
                        update('maxDistance', null);
                      } else {
                        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                        setLocal((prev) => ({ ...prev, minDistance: opt.min, maxDistance: opt.max }));
                      }
                    }} accessibilityLabel={`${t(opt.label)} trail length`} />
                  );
                })}
              </View>
            </View>
          )}

          {/* ── Distance From You ─────────────────────────────── */}
          <SectionHeader title={t('filters.distanceFromYou')} sectionKey="distance" />
          {!collapsedSections['distance'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {DISTANCE_FROM_YOU_OPTIONS.map((opt) => {
                  const isActive = local.minUserDistance === opt.min && local.maxUserDistance === opt.max;
                  return (
                    <Chip key={opt.key} label={t(opt.label)} active={isActive} onPress={() => {
                      if (isActive) {
                        update('minUserDistance', null);
                        update('maxUserDistance', null);
                      } else {
                        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                        setLocal((prev) => ({ ...prev, minUserDistance: opt.min, maxUserDistance: opt.max }));
                      }
                    }} accessibilityLabel={`Within ${t(opt.label)}`} />
                  );
                })}
              </View>
            </View>
          )}

          {/* ── Features / Tags ──────────────────────────────── */}
          <SectionHeader title={t('filters.features')} sectionKey="tags" count={local.tags.length || undefined} />
          {!collapsedSections['tags'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {TAG_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  return (
                    <Chip
                      key={opt.key}
                      label={t(opt.label)}
                      active={local.tags.includes(opt.key)}
                      onPress={() => {
                        const newTags = local.tags.includes(opt.key)
                          ? local.tags.filter((t) => t !== opt.key)
                          : [...local.tags, opt.key];
                        update('tags', newTags);
                      }}
                      icon={<Icon size={16} color={local.tags.includes(opt.key) ? theme.backgroundPrimary : theme.accentPrimary} strokeWidth={2} />}
                      accessibilityLabel={`Feature: ${t(opt.label)}`}
                    />
                  );
                })}
              </View>
            </View>
          )}

          {/* ── Dog-Friendly Traits ───────────────────────────── */}
          <SectionHeader title={t('filters.dogFriendlyTraits')} sectionKey="dog" />
          {!collapsedSections['dog'] && (
            <View style={s.sectionContent}>
              <Toggle
                label={t('filters.offLeashFriendly')}
                value={local.offLeash}
                onToggle={() => update('offLeash', local.offLeash ? null : true)}
                icon={<Dog size={20} color={local.offLeash ? theme.accentPrimary : theme.textMuted} strokeWidth={2} />}
              />
              <Toggle
                label={t('filters.lowDogTraffic')}
                value={local.lowDogTraffic}
                onToggle={() => update('lowDogTraffic', local.lowDogTraffic ? null : true)}
                icon={<Dog size={20} color={local.lowDogTraffic ? theme.accentPrimary : theme.textMuted} strokeWidth={2} />}
              />
              <Toggle
                label={t('filters.lowFootTraffic')}
                value={local.lowFootTraffic}
                onToggle={() => update('lowFootTraffic', local.lowFootTraffic ? null : true)}
                icon={<MapPin size={20} color={local.lowFootTraffic ? theme.accentPrimary : theme.textMuted} strokeWidth={2} />}
              />
              <Toggle
                label={t('filters.shadedLowExposure')}
                value={local.lowExposure}
                onToggle={() => update('lowExposure', local.lowExposure ? null : true)}
                icon={<Cloud size={20} color={local.lowExposure ? theme.accentPrimary : theme.textMuted} strokeWidth={2} />}
              />
              <Toggle
                label={t('filters.wildlifePresent')}
                value={local.wildlife}
                onToggle={() => update('wildlife', local.wildlife ? null : true)}
                icon={<TreePine size={20} color={local.wildlife ? theme.accentPrimary : theme.textMuted} strokeWidth={2} />}
              />
            </View>
          )}

          {/* ── Rating ────────────────────────────────────────── */}
          <SectionHeader title={t('filters.rating')} sectionKey="rating" />
          {!collapsedSections['rating'] && (
            <View style={s.sectionContent}>
              <View style={s.chipRow}>
                {RATING_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.key}
                    label={opt.label}
                    active={local.minRating === opt.key}
                    onPress={() => update('minRating', local.minRating === opt.key ? null : opt.key)}
                    icon={<Star size={14} color={local.minRating === opt.key ? theme.backgroundPrimary : theme.accentPrimary} fill={local.minRating === opt.key ? theme.backgroundPrimary : 'none'} strokeWidth={2} />}
                    accessibilityLabel={`Rating ${opt.label} and above`}
                  />
                ))}
              </View>
            </View>
          )}

          {/* Spacer for bottom button */}
          <View style={{ height: 80 }} />
        </ScrollView>

        {/* Apply button — paddingBottom includes the nav-bar inset
            (Samsung 3-button nav covered the button otherwise) */}
        <View style={[s.bottomBar, { paddingBottom: 20 + insets.bottom }]}>
          <TouchableOpacity style={s.applyButton} onPress={handleApply} activeOpacity={0.8} accessibilityRole="button" accessibilityLabel={t('filters.showTrailsCount', { count: displayCount })}>
            <Text style={s.applyButtonText}>
              {t('filters.showTrails', { count: displayCount })}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

// ─── Styles ──────────────────────────────────────────────────────

const s = {
  sheet: {
    flex: 1,
    backgroundColor: theme.backgroundPrimary,
  },
  dragHandleContainer: {
    alignItems: 'center' as const,
    paddingTop: 10,
    paddingBottom: 4,
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.borderSubtle,
  },
  header: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.borderSubtle,
  },
  headerTitle: {
    ...Typography.h3(theme.textPrimary),
  },
  clearAllText: {
    ...Typography.body(theme.accentPrimary),
    fontWeight: '600' as any,
  },
  scrollContent: {
    flex: 1,
    paddingHorizontal: 20,
  },
  sectionHeader: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingVertical: 16,
  },
  sectionHeaderLeft: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
  },
  sectionTitle: {
    ...Typography.label(theme.textPrimary),
  },
  sectionBadge: {
    backgroundColor: theme.accentPrimary,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
    minWidth: 20,
    alignItems: 'center' as const,
  },
  sectionBadgeText: {
    ...Typography.caption(theme.backgroundPrimary),
    fontWeight: '700' as any,
  },
  sectionContent: {
    paddingBottom: 8,
  },
  chipRow: {
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: 10,
  },
  chip: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
  },
  chipActive: {
    backgroundColor: 'rgba(255, 254, 119, 0.15)',
    borderColor: theme.accentPrimary,
  },
  chipText: {
    ...Typography.body(theme.textMuted),
    fontSize: 14,
  },
  chipTextActive: {
    color: theme.accentPrimary,
    fontWeight: '600' as any,
  },
  toggleRow: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: theme.borderSubtle,
  },
  toggleRowActive: {
    // Intentionally empty — switch colour change is enough
  },
  toggleLeft: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 12,
    flex: 1,
  },
  toggleLabel: {
    ...Typography.body(theme.textMuted),
    fontSize: 15,
  },
  toggleLabelActive: {
    color: theme.textPrimary,
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    backgroundColor: theme.backgroundSecondary,
    borderWidth: 1.5,
    borderColor: theme.borderSubtle,
    justifyContent: 'center' as const,
    alignItems: 'flex-start' as const,
    paddingLeft: 3,
  },
  toggleSwitchActive: {
    backgroundColor: theme.accentPrimary,
    borderColor: theme.accentPrimary,
    alignItems: 'flex-end' as const,
    paddingRight: 3,
    paddingLeft: 0,
  },
  toggleDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: theme.backgroundPrimary,
  },
  bottomBar: {
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 20,
    borderTopWidth: 1,
    borderTopColor: theme.borderSubtle,
    backgroundColor: theme.backgroundPrimary,
  },
  applyButton: {
    backgroundColor: theme.accentPrimary,
    borderRadius: 28,
    paddingVertical: 16,
    alignItems: 'center' as const,
  },
  applyButtonText: {
    ...Typography.button(theme.backgroundPrimary),
    fontWeight: '700' as any,
  },
};